import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  useTheme
} from '@mui/material';
import {
  Person as PersonIcon,
  Laptop as LaptopIcon,
  Shield as ShieldIcon
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIsAdmin } from '@stores/userStore';

type SettingsSidebarProps = {
  onClose?: () => void;
};

type SettingsSection = {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  adminOnly?: boolean;
};

const settingsSections: SettingsSection[] = [
  {
    id: 'profile',
    label: 'User Profile',
    icon: <PersonIcon />,
    path: '/settings/profile'
  },
  {
    id: 'application',
    label: 'Application',
    icon: <LaptopIcon />,
    path: '/settings/application'
  },
  {
    id: 'administration',
    label: 'Administration',
    icon: <ShieldIcon />,
    path: '/settings/administration',
    adminOnly: true
  }
];

export const SettingsSidebar = ({ onClose }: SettingsSidebarProps) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();

  // Filter sections based on user permissions
  const visibleSections = settingsSections.filter(
    section => !section.adminOnly || isAdmin
  );

  const handleSectionClick = (path: string) => {
    navigate(path);
    onClose?.();
  };

  return (
    <Paper
      sx={{
        width: 280,
        height: '100%',
        borderRadius: 0,
        borderRight: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Settings
        </Typography>
      </Box>

      <List sx={{ flex: 1, py: 2 }}>
        {!isAdmin && (
          <Box sx={{ px: 3, py: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Admin features require special permissions
            </Typography>
          </Box>
        )}
        {visibleSections.map(section => {
          const isActive =
            location.pathname === section.path ||
            (section.id === 'administration' &&
              location.pathname.startsWith('/settings/administration'));

          return (
            <ListItem key={section.id} disablePadding sx={{ px: 2, mb: 0.5 }}>
              <ListItemButton
                onClick={() => handleSectionClick(section.path)}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  px: 2,
                  backgroundColor: isActive ? 'primary.main' : 'transparent',
                  color: isActive ? 'primary.contrastText' : 'text.primary',
                  '&:hover': {
                    backgroundColor: isActive ? 'primary.dark' : 'action.hover'
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                <ListItemIcon
                  sx={{
                    color: 'inherit',
                    minWidth: 40
                  }}
                >
                  {section.icon}
                </ListItemIcon>
                <ListItemText
                  primary={section.label}
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontWeight: isActive ? 600 : 400
                    }
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Paper>
  );
};
